import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ExerciseFormSection } from "@/components/routines/workout-plans/components/exercise/ExerciseFormSection";
import { ExerciseListSection } from "@/components/routines/workout-plans/components/exercise/ExerciseListSection";
import { RoutineShimmer } from "@/components/routines/RoutineShimmer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const EditarRutinaPortafolio = () => {
  const { routineId } = useParams();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAllowed =
    profile?.role === "profesor" ||
    profile?.role === "manager" ||
    profile?.role === "admin";

  const { data: routine, isLoading, refetch } = useQuery({
    queryKey: ["portfolio-routine", routineId],
    queryFn: async () => {
      if (!routineId) return null;

      const { data: r, error: rErr } = await supabase
        .from("routines")
        .select("id, name, image_url, user_id")
        .eq("id", routineId)
        .single();
      if (rErr) throw rErr;

      const { data: exercises, error: eErr } = await supabase
        .from("exercises")
        .select(
          "id, name, sets, reps, weight, position, routine_id, set_data, exercise_rest_time, set_rest_time, video_url"
        )
        .eq("routine_id", routineId)
        .order("position", { ascending: true });
      if (eErr) throw eErr;

      return {
        ...r,
        exercises: (exercises || []).map((e) => ({
          ...e,
          exercise_rest_time:
            e.exercise_rest_time !== null ? Number(e.exercise_rest_time) : null,
          set_rest_time:
            e.set_rest_time !== null ? Number(e.set_rest_time) : null,
          set_data: Array.isArray(e.set_data) ? e.set_data : [],
        })),
      };
    },
    enabled: !!routineId,
    staleTime: 10000,
  });

  useEffect(() => {
    if (routine?.name) setName(routine.name);
  }, [routine?.name]);

  const handleExerciseChange = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ["teacher-routines-portfolio"] });
  };

  const handleSaveName = async () => {
    if (!routineId || !name.trim() || name === routine?.name) return;
    setSavingName(true);
    try {
      const { error } = await supabase
        .from("routines")
        .update({ name: name.trim() })
        .eq("id", routineId);
      if (error) throw error;
      toast({ title: "Rutina actualizada", variant: "success" });
      refetch();
      queryClient.invalidateQueries({
        queryKey: ["teacher-routines-portfolio"],
      });
    } catch (e) {
      console.error(e);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar el nombre",
      });
    } finally {
      setSavingName(false);
    }
  };

  const handleDeleteRoutine = async () => {
    if (!routineId) return;
    setDeleting(true);
    try {
      // Borrar dependencias y rutina (mismo patrón que removeRoutineUtils)
      const { error: exErr } = await supabase
        .from("exercises")
        .delete()
        .eq("routine_id", routineId);
      if (exErr) throw exErr;

      const { error: dErr } = await supabase
        .from("routine_days")
        .delete()
        .eq("routine_id", routineId);
      if (dErr) throw dErr;

      const { error: linkErr } = await supabase
        .from("workout_plan_routines")
        .delete()
        .eq("routine_id", routineId);
      if (linkErr) throw linkErr;

      const { error: rErr } = await supabase
        .from("routines")
        .delete()
        .eq("id", routineId);
      if (rErr) throw rErr;

      toast({ title: "Rutina eliminada", variant: "success" });
      queryClient.invalidateQueries({
        queryKey: ["teacher-routines-portfolio"],
      });
      navigate("/rutina/portafolio");
    } catch (e) {
      console.error(e);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar la rutina",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (profile && !isAllowed) {
    return (
      <div className="min-h-screen bg-background text-primary p-6 text-center">
        <p className="mb-4">Sección solo para profesores.</p>
        <Button variant="outline" onClick={() => navigate("/rutina")}>
          Volver
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-primary container mx-auto px-4 py-6">
        <RoutineShimmer />
      </div>
    );
  }

  if (!routine) {
    return (
      <div className="min-h-screen bg-background text-primary p-6 text-center">
        <p className="mb-4">Rutina no encontrada.</p>
        <Button variant="outline" onClick={() => navigate("/rutina/portafolio")}>
          Volver al portafolio
        </Button>
      </div>
    );
  }

  // Solo el dueño puede editar
  if (routine.user_id !== profile?.id && profile?.role !== "admin") {
    return (
      <div className="min-h-screen bg-background text-primary p-6 text-center">
        <p className="mb-4">No tenés permisos para editar esta rutina.</p>
        <Button variant="outline" onClick={() => navigate("/rutina/portafolio")}>
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary">
      <main className="container pt-16 mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/rutina/portafolio")}
            className="text-primary hover:text-primary/80"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-primary">
            Editar rutina
          </h1>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-primary/20 bg-primary/5 backdrop-blur-sm p-4">
            <label className="text-sm text-primary/70 mb-2 block">
              Nombre de la rutina
            </label>
            <div className="flex gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre"
              />
              <Button
                onClick={handleSaveName}
                disabled={
                  savingName || !name.trim() || name === routine.name
                }
              >
                <Save className="h-4 w-4 mr-1" />
                Guardar
              </Button>
            </div>
          </div>

          <div className="rounded-xl border border-primary/20 bg-primary/5 backdrop-blur-sm p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Ejercicios</h2>
              <Button
                onClick={() => setShowExerciseForm((s) => !s)}
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-1" />
                {showExerciseForm ? "Cancelar" : "Añadir ejercicio"}
              </Button>
            </div>

            <ExerciseFormSection
              showExerciseForm={showExerciseForm}
              routineId={routine.id}
              onExerciseAdded={() => {
                setShowExerciseForm(false);
                handleExerciseChange();
              }}
            />

            <ExerciseListSection
              exercises={routine.exercises || []}
              routineId={routine.id}
              onExerciseChange={handleExerciseChange}
              showControls={true}
              isPersonalPlan={true}
            />
          </div>

          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-destructive">Eliminar rutina</h3>
              <p className="text-sm text-primary/60">
                Esta acción no se puede deshacer.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={deleting}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                  <AlertDialogDescription>
                    La rutina y todos sus ejercicios serán eliminados
                    permanentemente.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteRoutine}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditarRutinaPortafolio;
